import api from './api';

export interface LogoServiceResponse {
    hasLogo: boolean;
    logoUrl?: string;
    error?: string;
}

class LogoService {
    private logoObjectUrl: string | null = null;

    /**
     * Check if user has an existing logo and return it as a blob URL
     */
    async checkExistingLogo(): Promise<LogoServiceResponse> {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                return { hasLogo: false, error: 'No authentication token' };
            }

            const response = await api.get("/user/logo", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                responseType: 'blob'
            });

            // If we get a response with data, create a URL for the blob
            if (response.data && response.data.size > 0) {
                // Clean up previous object URL if it exists
                this.cleanupObjectUrl();

                const logoBlob = new Blob([response.data]);
                this.logoObjectUrl = URL.createObjectURL(logoBlob);

                return {
                    hasLogo: true,
                    logoUrl: this.logoObjectUrl
                };
            } else {
                return { hasLogo: false };
            }
        } catch (error: any) {
            // Status 204 means no logo exists, which is expected
            if (error.response?.status === 204) {
                return { hasLogo: false };
            }

            console.error("Error checking existing logo:", error);
            return {
                hasLogo: false,
                error: error.response?.data?.message || 'Failed to check existing logo'
            };
        }
    }

    /**
     * Upload a new logo file
     */
    async uploadLogo(file: File): Promise<{ success: boolean; error?: string }> {
        try {
            // Validate file type - match the server validation
            if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
                return { success: false, error: 'Only PNG and JPG images are allowed' };
            }

            // Validate file size (2MB limit to match server)
            if (file.size > 2 * 1024 * 1024) {
                return { success: false, error: 'File size must be less than 2MB' };
            }

            const token = localStorage.getItem("token");
            if (!token) {
                return { success: false, error: 'No authentication token' };
            }

            const formData = new FormData();
            formData.append('logo', file);

            await api.post("/user/logo", formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            return { success: true };
        } catch (error: any) {
            console.error("Error uploading logo:", error);
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to upload logo'
            };
        }
    }

    /**
     * Remove the user's logo
     */
    async removeLogo(): Promise<{ success: boolean; error?: string }> {
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                return { success: false, error: 'No authentication token' };
            }

            await api.delete("/user/logo", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // Clean up the object URL
            this.cleanupObjectUrl();

            return { success: true };
        } catch (error: any) {
            console.error("Error removing logo:", error);
            return {
                success: false,
                error: error.response?.data?.message || 'Failed to remove logo'
            };
        }
    }

    /**
     * Clean up object URL to prevent memory leaks
     */
    private cleanupObjectUrl(): void {
        if (this.logoObjectUrl) {
            URL.revokeObjectURL(this.logoObjectUrl);
            this.logoObjectUrl = null;
        }
    }

    /**
     * Clean up any remaining object URLs (call this when component unmounts)
     */
    cleanup(): void {
        this.cleanupObjectUrl();
    }
}

// Export a singleton instance
export const logoService = new LogoService();
export default logoService;
